import type { CSSProperties } from 'react';
import { formatColorArrayToRGBAString } from './internal/utils/formatColorArrayToRGBAString';
import type { UserPreferences } from './styleApplicator.types';
import type { CanvasSettings } from './themeOutput.types';

/**
 * The canvas "container settings" → CSS mapping, extracted verbatim from the legacy
 * applicator's applySettingsToContainer (theme-system StyleApplicator.ts) so the React
 * renderer paints the frame exactly the way the imperative one did.
 */
export const computeContainerStyle = (settings: CanvasSettings, preferences: UserPreferences, shouldApplyTextColor: boolean): CSSProperties | undefined => {
  const { colors, backgroundTranslucency } = settings;
  const style: CSSProperties = {};

  if (backgroundTranslucency.isEnabled) {
    if (!backgroundTranslucency.supportCustomization || (backgroundTranslucency.supportCustomization && preferences.backgroundTranslucency.isEnabled)) {
      // Background translucency will typically not be used in conjunction with custom
      // background color, so background translucency takes precedence.
      style.backgroundColor = formatColorArrayToRGBAString(backgroundTranslucency.backgroundColor);
    }
  } else if (colors.isEnabled) {
    // Colors will always be enabled, but keep this here for now.
    if (colors.supportCustomization && preferences.customBackgroundColor.isEnabled) {
      style.backgroundColor = formatColorArrayToRGBAString(preferences.customBackgroundColor.backgroundColor);
      if (shouldApplyTextColor) {
        style.color = formatColorArrayToRGBAString(preferences.customBackgroundColor.textColor);
      }
    } else {
      // Use suggested colors. If the designer doesn't provide any, the style-engine
      // defers to defaults - white background + black-ish text.
      style.backgroundColor = formatColorArrayToRGBAString(colors.backgroundColor);
      if (shouldApplyTextColor) {
        style.color = formatColorArrayToRGBAString(colors.textColor);
      }
    }
  }

  return Object.keys(style).length > 0 ? style : undefined;
};
